import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, CircularProgress, Typography} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import {T, useTranslate} from "@tolgee/react";
import {red} from "@material-ui/core/colors";
import {addMinutes, format, Locale} from "date-fns";
import {jwtDecode} from "jwt-decode";
import {RecoveryCodeAlertOutcome} from "@evennt-tech/api-v2-external";
import useSearchParam from "hooks/useSearchParam";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import {apiV2} from "api/firebase";
import {getErrorMessage} from "tools/error";
import KeyIcon from "view/images/KeyIcon";
import FlexBox from "view/components/Layout/FlexBox";
import Alert from "view/components/Alert";
import Copyright from "view/components/Copyright";
import useStyles from "./styles";

type View =
  | {phase: "checking"}
  | {phase: "pending"}
  | {phase: "expired"}
  | {
      phase: "resolved";
      outcome: RecoveryCodeAlertOutcome;
      resolvedAt: string;
      alreadyResolved: boolean;
    };

const isTokenExpired = (token: string): boolean => {
  try {
    const {exp} = jwtDecode<{exp?: number}>(token);
    return typeof exp === "number" && exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

// date-fns has no timezone support without date-fns-tz, so shift by the offset
// to make the local fields read as UTC before formatting.
const formatResolvedAt = (iso: string, locale: Locale): string => {
  const date = new Date(iso);
  const asUtc = addMinutes(date, date.getTimezoneOffset());
  return `${format(asUtc, "d MMMM yyyy, HH:mm", {locale})} UTC`;
};

const RecoveryCodeConfirmation: React.FC = () => {
  const classes = useStyles();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();

  const token = useSearchParam("token");
  const [view, setView] = useState<View>({phase: "checking"});
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState<"deny" | "confirm" | null>(
    null
  );

  useEffect(() => {
    if (!token) return undefined;

    if (isTokenExpired(token)) {
      setView({phase: "expired"});
      return undefined;
    }

    let active = true;
    apiV2.recoveryCode
      .alertStatus({token})
      .then((res) => {
        if (!active) return;
        setView(
          res.resolved && res.outcome && res.resolvedAt
            ? {
                phase: "resolved",
                outcome: res.outcome,
                resolvedAt: res.resolvedAt,
                alreadyResolved: true,
              }
            : {phase: "pending"}
        );
      })
      .catch(() => {
        if (active) setView({phase: "expired"});
      });

    return () => {
      active = false;
    };
  }, [token]);

  const handleAction = useCallback(
    async (action: "deny" | "confirm") => {
      if (!token) return;
      setError("");
      setIsSubmitting(action);
      try {
        const res =
          action === "deny"
            ? await apiV2.recoveryCode.denyAccess({token})
            : await apiV2.recoveryCode.confirmAccess({token});
        setView({
          phase: "resolved",
          outcome: res.outcome,
          resolvedAt: res.resolvedAt,
          alreadyResolved: res.alreadyResolved,
        });
      } catch (e) {
        setError(getErrorMessage(e));
      } finally {
        setIsSubmitting(null);
      }
    },
    [token]
  );

  const closePage = () => window.close();

  const renderResolved = (resolved: Extract<View, {phase: "resolved"}>) => {
    const denied = resolved.outcome === RecoveryCodeAlertOutcome.Denied;

    let headline: string;

    if (resolved.alreadyResolved) {
      headline = denied
        ? t("global.recovery_code.already_denied")
        : t("global.recovery_code.already_confirmed");
    } else {
      headline = denied
        ? t("global.recovery_code.denied")
        : t("global.recovery_code.confirmed");
    }

    return (
      <FlexBox column gap={1} alignCenter justifyCenter>
        <FlexBox gap={1} alignCenter justifyCenter>
          {denied ? (
            <CancelIcon color="primary" fontSize="small" />
          ) : (
            <CheckCircleIcon color="primary" fontSize="small" />
          )}
          <Typography color="primary" style={{fontWeight: 500}}>
            {headline}
          </Typography>
        </FlexBox>
        <Typography color="textSecondary" align="center">
          <T
            keyName="global.recovery_code.link_already_used"
            params={{date: formatResolvedAt(resolved.resolvedAt, locale)}}
          />
        </Typography>
      </FlexBox>
    );
  };

  const renderContent = () => {
    if (!token) {
      return (
        <FlexBox gap={1} alignCenter justifyCenter>
          <CancelIcon htmlColor={red[500]} />
          <Typography variant="h4" color="error">
            <T keyName="global.recovery_code.missing_link" />
          </Typography>
        </FlexBox>
      );
    }

    switch (view.phase) {
      case "checking":
        return (
          <FlexBox justifyCenter>
            <CircularProgress />
          </FlexBox>
        );
      case "expired":
        return (
          <Alert severity="error">
            <T keyName="global.recovery_code.link_expired" />
          </Alert>
        );
      case "resolved":
        return renderResolved(view);
      default:
        return (
          <>
            <Typography className={classes.message}>
              <T keyName="global.recovery_code.prompt" />
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </>
        );
    }
  };

  const renderActions = () => {
    if (token && view.phase === "checking") {
      return null;
    }

    if (token && view.phase === "pending") {
      return (
        <FlexBox justifyBetween>
          <Button
            variant="outlined"
            style={{color: red[500], borderColor: red[500]}}
            disabled={isSubmitting !== null}
            endIcon={
              isSubmitting === "deny" ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            onClick={() => handleAction("deny")}
          >
            {t("global.recovery_code.deny_access")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitting !== null}
            endIcon={
              isSubmitting === "confirm" ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            onClick={() => handleAction("confirm")}
          >
            {t("global.recovery_code.it_was_me")}
          </Button>
        </FlexBox>
      );
    }

    return (
      <FlexBox justifyCenter>
        <Button variant="contained" color="primary" onClick={closePage}>
          {t("global.recovery_code.close_page")}
        </Button>
      </FlexBox>
    );
  };

  return (
    <>
      <Box className={classes.root}>
        <FlexBox column gap={3} className={classes.content}>
          <FlexBox column gap={2} fullWidth justifyCenter alignCenter>
            <KeyIcon />
            <Typography variant="h2" className={classes.title}>
              <T keyName="global.login.sign_in" />
            </Typography>
          </FlexBox>
          {renderContent()}
          {renderActions()}
        </FlexBox>
      </Box>
      <FlexBox className={classes.copyright}>
        <Copyright />
      </FlexBox>
    </>
  );
};

export default RecoveryCodeConfirmation;
